import React, { useEffect, useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { CopyText } from "../../components/Utilities/CopyText";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { usePassConfigurationDisplayName } from "../../hooks/usePassConfigurationDisplayName";
import { useToast } from "../../hooks/useToast";
import { formatHistoryVersion } from "../../util/formatHistoryVersion";
import { isPassBuilderPublishedMessage } from "../../util/passBuilderParentMessages";
import DateUtil from "../../util/DateUtil";

const PASS_CONFIGURATION_DOCUMENT_TYPE = "PassConfiguration";

const sortHistoryItems = (
  items: CherryHubAdminApi.DocumentHistoryItem[]
): CherryHubAdminApi.DocumentHistoryItem[] => {
  return [...items].sort((a, b) => {
    const versionDiff = (b.Version ?? 0) - (a.Version ?? 0);
    if (versionDiff !== 0) {
      return versionDiff;
    }

    const aUpdated = a.Meta?.DateUpdated
      ? new Date(a.Meta.DateUpdated).getTime()
      : 0;
    const bUpdated = b.Meta?.DateUpdated
      ? new Date(b.Meta.DateUpdated).getTime()
      : 0;
    return bUpdated - aUpdated;
  });
};

export const PassEditor = () => {
  const { businessId, passConfigurationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { showErrorToast } = useToast();
  const historyId = searchParams.get("historyId");
  const { displayName, refresh: refreshDisplayName } =
    usePassConfigurationDisplayName(businessId, passConfigurationId);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isPassBuilderPublishedMessage(event, PASS_BUILDER_URL)) {
        return;
      }

      refreshDisplayName();
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [refreshDisplayName]);

  const [historyItems, setHistoryItems] = useState<
    CherryHubAdminApi.DocumentHistoryItem[]
  >([]);

  useEffect(() => {
    if (!historyId || !businessId || !passConfigurationId) {
      setHistoryItems([]);
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      try {
        const result = await apiClient.getDocumentHistory(
          businessId,
          PASS_CONFIGURATION_DOCUMENT_TYPE,
          passConfigurationId
        );

        if (cancelled) {
          return;
        }

        if (!result.ok) {
          showErrorToast(
            result.message || "Failed to load pass configuration history."
          );
          setHistoryItems([]);
          return;
        }

        setHistoryItems(result.data.Results ?? []);
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Load pass configuration history failed:", error);
        showErrorToast(
          "An error occurred while loading pass configuration history."
        );
        setHistoryItems([]);
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [apiClient, businessId, historyId, passConfigurationId, showErrorToast]);

  const sortedHistoryItems = useMemo(
    () => sortHistoryItems(historyItems),
    [historyItems]
  );

  const selectableHistoryItems = useMemo(
    () => sortedHistoryItems.filter((item) => !!item.HistoryId),
    [sortedHistoryItems]
  );

  const currentHistoryItem = useMemo(
    () =>
      selectableHistoryItems.find((item) => item.HistoryId === historyId) ??
      null,
    [historyId, selectableHistoryItems]
  );

  const iframeSrc = useMemo(() => {
    const base = `${PASS_BUILDER_URL}/businesses/${businessId}/pass-designer/${passConfigurationId}`;
    if (!historyId) {
      return base;
    }

    return `${base}?historyId=${encodeURIComponent(historyId)}`;
  }, [businessId, passConfigurationId, historyId]);

  const handleVersionChange = (nextHistoryId: string) => {
    if (!businessId || !passConfigurationId || nextHistoryId === historyId) {
      return;
    }

    navigate(
      `/admin/businesses/${businessId}/pass-configurations/${passConfigurationId}?historyId=${encodeURIComponent(
        nextHistoryId
      )}`
    );
  };

  const versionLabel = formatHistoryVersion(currentHistoryItem?.Version);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {historyId ? "Preview Pass Configuration" : "Edit Pass Configuration"}
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/pass-configurations`}
              >
                Pass Configurations
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage={!historyId}>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/pass-configurations/${passConfigurationId}`}
              >
                {passConfigurationId}
              </BreadcrumbLink>
            </BreadcrumbItem>
            {historyId && (
              <BreadcrumbItem isCurrentPage>
                {selectableHistoryItems.length > 1 ? (
                  <Menu>
                    <MenuButton
                      as={Button}
                      variant="ghost"
                      size="sm"
                      fontWeight="normal"
                      rightIcon={<ChevronDownIcon />}
                      px={2}
                      minW="auto"
                      height="auto"
                      lineHeight="inherit"
                    >
                      [{versionLabel}]
                    </MenuButton>
                    <MenuList minW="120px" zIndex={10}>
                      {selectableHistoryItems.map((item) => (
                        <MenuItem
                          key={item.HistoryId!}
                          fontWeight={
                            item.HistoryId === historyId ? "semibold" : "normal"
                          }
                          onClick={() => handleVersionChange(item.HistoryId!)}
                        >
                          {formatHistoryVersion(item.Version)}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                ) : (
                  <Text as="span">[{versionLabel}]</Text>
                )}
              </BreadcrumbItem>
            )}
          </BusinessBreadcrumbs>

          <Box pb={4} w="100%">
            {displayName && (
              <Text fontSize="xl" color="cherryUi.600" mb={1}>
                <b>{displayName}</b>
              </Text>
            )}
            <CopyText text={passConfigurationId} hover adjustMargin>
              <Text fontSize="md" color="cherryUi.600">
                {passConfigurationId}
              </Text>
            </CopyText>

            {historyId && (
              <Box mt={2}>
                <Flex alignItems="center" gap={8} flexWrap="wrap">
                  <Text fontSize="sm" color="gray.600">
                    Pass Configuration
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {versionLabel} [Read only]
                  </Text>
                </Flex>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Created{" "}
                  {DateUtil.getLocalDateOnlyFormatFromString(
                    currentHistoryItem?.Meta?.DateCreated
                  ) || "—"}{" "}
                  • Last updated{" "}
                  {DateUtil.getLocalDateTimeShortFormatFromString(
                    currentHistoryItem?.Meta?.DateUpdated
                  ) || "—"}
                </Text>
              </Box>
            )}
          </Box>

          <iframe
            key={historyId ?? "current"}
            src={iframeSrc}
            width="100%"
            style={{ height: "80vh" }}
            title={
              historyId
                ? "Historical pass configuration preview"
                : "Pass configuration editor"
            }
          />
        </VStack>
      </PageContent>
    </>
  );
};
